import { useState, useCallback } from 'react';
import type { AggregatedFreeCashFlow as AggregatedFreeCashFlowType } from 'types/evaluation';
import type {
  CalculateRequest,
  CalculationConfig,
  CalculationDetail,
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  DpsScheduleResponse,
  EvaluationCalculation,
  RenameWorkspaceRequest,
  RenameWorkspaceResponse,
  SaveRequest,
  SaveResponse,
  CalculationsResponse,
} from './types';
import { buildCalculateRequest } from './util';

// ---------------------------------------------------------------------------
// useWorkspaceActions — workspace CRUD handlers + associated modal/loading state
// ---------------------------------------------------------------------------

// Source keys scope notifications to their workflow so a successful retry
// can clear the workflow's stale errors without disturbing notifications
// from unrelated workflows. See `useNotifications`.
const NOTIFICATION_SOURCES = {
  workspaceCreate: 'workspace-create',
  workspaceRename: 'workspace-rename',
  save: 'save',
  loadCalcs: 'load-calcs',
} as const;

interface UseWorkspaceActionsParams {
  packageId: number;
  // Atom state needed by handlers
  selectedWorkspaceId: number | null;
  activeWorkspaceId: number | null;
  selectedScenarioId: number | null;
  selectedCalcId: number | null;
  selectedPriceDeckId: number | null;
  currentConfig: CalculationConfig;
  currentDetail: CalculationDetail | null;
  isZeroSum: boolean;
  dpsScheduleResult: DpsScheduleResponse | null;
  calculationsCache: Record<number, EvaluationCalculation[]>;
  // Setters
  setSelectedWorkspaceId: (id: number | null) => void;
  setSelectedScenarioId: (id: number | null) => void;
  setSelectedEvalMode: (mode: 'case' | 'legacy') => void;
  setSelectedEvaluationCaseId: (id: number | null) => void;
  setSelectedCalcId: (id: number | null) => void;
  setCurrentDetail: (
    d:
      | CalculationDetail
      | null
      | ((prev: CalculationDetail | null) => CalculationDetail | null)
  ) => void;
  setResultData: (d: AggregatedFreeCashFlowType | null) => void;
  setResultError: (e: string) => void;
  resetExecutiveSummaryPricing: () => void;
  // External hooks/callbacks
  pushNotification: (msg: string, source?: string) => void;
  clearNotifications: () => void;
  clearNotificationsBySource: (source: string) => void;
  handleDpsChange: (
    dpsId: number,
    scenarioIdOverride?: number | null
  ) => Promise<void>;
  triggerWorkspaces: () => void;
  triggerCalculation: (
    req: CalculateRequest,
    config?: CalculationConfig
  ) => void;
  // Mutation atom setters
  saveEvaluation: (
    calcId: number,
    payload: SaveRequest
  ) => Promise<SaveResponse>;
  createWorkspace: (
    pkgId: number,
    payload?: CreateWorkspaceRequest
  ) => Promise<CreateWorkspaceResponse>;
  renameWorkspace: (
    wsId: number,
    payload: RenameWorkspaceRequest
  ) => Promise<RenameWorkspaceResponse>;
  fetchCalculations: (
    pkgId: number,
    wsId: number
  ) => Promise<CalculationsResponse>;
  setLoadingCalcsWsId: (id: number | null) => void;
}

interface UseWorkspaceActionsReturn {
  // INS-8827: scenarioId replaces eval2Id as the source-of-truth for the
  // calc's scenario_id. The DPS pick still kicks off scheduling.
  handleCreateWorkspace: (
    name?: string,
    scenarioId?: number,
    dpsId?: number,
    evaluationCaseId?: number
  ) => Promise<void>;
  handleCalculate: () => void;
  handleSave: (description: string, notes: string) => Promise<void>;
  handleWorkspaceExpand: (wsId: number) => void;
  handleRename: (newName: string) => Promise<void>;
  // Loading state managed internally
  isCreateLoading: boolean;
  isSaveLoading: boolean;
  isRenameLoading: boolean;
  // Modal state managed internally
  isCreateModalOpen: boolean;
  setIsCreateModalOpen: (v: boolean) => void;
  isSaveModalOpen: boolean;
  setIsSaveModalOpen: (v: boolean) => void;
  isRenameModalOpen: boolean;
  setIsRenameModalOpen: (v: boolean) => void;
}

/**
 * Encapsulates workspace CRUD handlers (create, calculate, save, expand,
 * rename) along with the modal open/close state and loading booleans they
 * manage. Accepts notification callbacks from the `useNotifications` hook
 * rather than re-deriving them.
 *
 * @param {UseWorkspaceActionsParams} params - All state/setters the handlers need.
 * @return {UseWorkspaceActionsReturn} Handler functions, loading flags, and modal state.
 */
export const useWorkspaceActions = (
  params: UseWorkspaceActionsParams
): UseWorkspaceActionsReturn => {
  const {
    packageId,
    selectedWorkspaceId,
    activeWorkspaceId,
    selectedScenarioId,
    selectedCalcId,
    selectedPriceDeckId,
    currentConfig,
    currentDetail,
    isZeroSum,
    dpsScheduleResult,
    calculationsCache,
    setSelectedWorkspaceId,
    setSelectedScenarioId,
    setSelectedEvalMode,
    setSelectedEvaluationCaseId,
    setSelectedCalcId,
    setCurrentDetail,
    setResultData,
    setResultError,
    resetExecutiveSummaryPricing,
    pushNotification,
    clearNotifications,
    clearNotificationsBySource,
    handleDpsChange,
    triggerWorkspaces,
    triggerCalculation,
    saveEvaluation,
    createWorkspace,
    renameWorkspace,
    fetchCalculations,
    setLoadingCalcsWsId,
  } = params;

  // ---------------------------------------------------------------------------
  // Internal state — loading flags + modal open/close
  // ---------------------------------------------------------------------------
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // handleCreateWorkspace
  // ---------------------------------------------------------------------------

  /**
   * Creates a new workspace and (when a DPS is provided) kicks off DPS
   * scheduling. The actual calculation is triggered later by the user
   * via the Calculate button — INS-8827 makes DPS scheduling mandatory
   * before calculate, so we no longer fire calculate from this path.
   *
   * @param {string} [name] - Optional workspace name from the modal.
   * @param {number} [scenarioId] - The Scenario id picked in the modal.
   * @param {number} [dpsId] - The DPS id picked in the modal.
   */
  const handleCreateWorkspace = useCallback(
    async (
      name?: string,
      scenarioId?: number,
      dpsId?: number,
      evaluationCaseId?: number
    ) => {
      setIsCreateLoading(true);
      try {
        const payload = name?.trim() ? { name: name.trim() } : undefined;
        const ws = await createWorkspace(packageId, payload);
        if (scenarioId) {
          setSelectedScenarioId(scenarioId);
        }
        setSelectedWorkspaceId(ws.id);
        setIsCreateModalOpen(false);
        triggerWorkspaces();
        clearNotificationsBySource(NOTIFICATION_SOURCES.workspaceCreate);
        resetExecutiveSummaryPricing();

        if (evaluationCaseId) {
          // Case mode: set eval mode atoms, skip DPS scheduling.
          // CaseResolver handles all domain resolution at calculate time.
          setSelectedEvalMode('case');
          setSelectedEvaluationCaseId(evaluationCaseId);
          setSelectedCalcId(null);
          setCurrentDetail(null);
          setResultData(null);
          setResultError('');
        } else if (dpsId) {
          // Legacy mode: trigger DPS scheduling (async Pusher flow).
          setSelectedEvalMode('legacy');
          setSelectedEvaluationCaseId(null);
          setSelectedCalcId(null);
          setCurrentDetail(null);
          setResultData(null);
          setResultError('');
          handleDpsChange(dpsId, scenarioId);
        }
      } catch {
        pushNotification(
          'Failed to create workspace',
          NOTIFICATION_SOURCES.workspaceCreate
        );
      } finally {
        setIsCreateLoading(false);
      }
    },
    [
      createWorkspace,
      packageId,
      setSelectedScenarioId,
      setSelectedEvalMode,
      setSelectedEvaluationCaseId,
      setSelectedWorkspaceId,
      setSelectedCalcId,
      triggerWorkspaces,
      pushNotification,
      clearNotificationsBySource,
      handleDpsChange,
      setCurrentDetail,
      setResultData,
      setResultError,
      resetExecutiveSummaryPricing,
    ]
  );

  // ---------------------------------------------------------------------------
  // handleCalculate
  // ---------------------------------------------------------------------------

  /**
   * Builds a CalculateRequest from the current working copy and triggers
   * the calculation.
   */
  const handleCalculate = useCallback(() => {
    clearNotifications();
    setResultError('');

    if (isZeroSum) {
      handleCreateWorkspace();
      return;
    }

    // Use selectedWorkspaceId (what the user chose) rather than activeWorkspaceId
    // (derived from which workspace owns the current calc). After creating a new
    // workspace with DPS, activeWorkspaceId still points to the old workspace
    // because no calc exists in the new one yet.
    const targetWorkspaceId = selectedWorkspaceId ?? activeWorkspaceId;
    if (!targetWorkspaceId) return;

    // INS-8827: scenario_id is mandatory and must be sourced from the
    // Scenario picker. Fall back to the hydrated detail's scenario id when
    // editing a legacy calc whose picker state hasn't been seeded yet
    // (e.g. on first load before the user touched the picker).
    const scenarioId =
      selectedScenarioId ?? currentDetail?.scenario?.id ?? null;
    if (scenarioId === null) return;

    // Shared params for both case and legacy modes
    const commonParams = {
      workspaceId: targetWorkspaceId,
      scenarioId,
      packageId,
      effectiveDate: currentConfig.effectiveDate,
      slotVolumeSource: currentConfig.slotVolumeSource,
      slotAfeSource: currentConfig.slotAfeSource,
      priceDeckId: selectedPriceDeckId,
      dcaScenarioId: currentConfig.dcaScenarioId,
      slotVolumeConfigurations: currentConfig.slotVolumeConfigurations,
      slotAfeConfigurations: currentConfig.slotAfeConfigurations,
      slotCostDateConfigurations: currentConfig.slotCostDateConfigurations,
      slotDateConfigurations: currentConfig.slotDateConfigurations,
    };

    let calcRequest;

    if (currentConfig.evalMode === 'case') {
      // Case mode: CaseResolver handles DPS/Tax/DRS/Differential — no pre-scheduling needed
      if (!currentConfig.evaluationCaseId) return;
      calcRequest = buildCalculateRequest({
        ...commonParams,
        evalMode: 'case',
        evaluationCaseId: currentConfig.evaluationCaseId,
      });
    } else {
      // Legacy mode: individual scenario IDs + DPS pre-scheduling required
      const hasDps = currentConfig.dpsId !== null && dpsScheduleResult !== null;
      if (!hasDps) return;
      calcRequest = buildCalculateRequest({
        ...commonParams,
        dpsId: currentConfig.dpsId,
        slotsBlobPath: dpsScheduleResult!.slotsBlobPath,
        scheduledSlotsBlobPath: dpsScheduleResult!.scheduledSlotsBlobPath,
        drsId: currentConfig.drsId,
        differentialScenarioId: currentConfig.differentialScenarioId,
        taxScenarioId: currentConfig.taxScenarioId,
        expenseScenarioId: currentConfig.expenseScenarioId,
      });
    }

    // Clear stale result data so the skeleton stays visible until effect #4
    // provides both new resultData and new calculationId in a single render,
    // preventing a double cashflows-by-month fetch with the old UUID.
    setResultData(null);

    triggerCalculation(calcRequest);
  }, [
    clearNotifications,
    isZeroSum,
    handleCreateWorkspace,
    currentDetail,
    selectedScenarioId,
    selectedWorkspaceId,
    activeWorkspaceId,
    currentConfig,
    selectedPriceDeckId,
    packageId,
    triggerCalculation,
    dpsScheduleResult,
    setResultError,
    setResultData,
  ]);

  // ---------------------------------------------------------------------------
  // handleSave
  // ---------------------------------------------------------------------------

  /**
   * Saves the current calculation permanently with an optional description.
   *
   * @param {string} description - User-provided description from the modal.
   * @param {string} notes - User-provided notes from the modal.
   */
  const handleSave = useCallback(
    async (description: string, notes: string) => {
      if (!selectedCalcId || !activeWorkspaceId) return;
      setIsSaveLoading(true);
      try {
        await saveEvaluation(selectedCalcId, {
          description: description || undefined,
          notes: notes || undefined,
        });
        setCurrentDetail((prev) => {
          return prev
            ? {
                ...prev,
                saved: true,
                description: description || null,
                notes: notes || null,
              }
            : prev;
        });
        fetchCalculations(packageId, activeWorkspaceId);
        setIsSaveModalOpen(false);
        // Wipe stale save errors from prior failed attempts.
        clearNotificationsBySource(NOTIFICATION_SOURCES.save);
      } catch {
        pushNotification(
          'Failed to save evaluation',
          NOTIFICATION_SOURCES.save
        );
      } finally {
        setIsSaveLoading(false);
      }
    },
    [
      selectedCalcId,
      activeWorkspaceId,
      saveEvaluation,
      fetchCalculations,
      packageId,
      pushNotification,
      clearNotificationsBySource,
      setCurrentDetail,
    ]
  );

  // ---------------------------------------------------------------------------
  // handleWorkspaceExpand
  // ---------------------------------------------------------------------------

  /**
   * Called when a workspace accordion is expanded. Selects the workspace
   * and fetches its calculations after the accordion animation completes.
   *
   * @param {number} wsId - The workspace ID that was expanded.
   */
  const handleWorkspaceExpand = useCallback(
    (wsId: number) => {
      setSelectedWorkspaceId(wsId);
      if (!calculationsCache[wsId]) {
        setLoadingCalcsWsId(wsId);
        fetchCalculations(packageId, wsId)
          .then(() =>
            clearNotificationsBySource(NOTIFICATION_SOURCES.loadCalcs)
          )
          .catch(() =>
            pushNotification(
              'Failed to load calculations',
              NOTIFICATION_SOURCES.loadCalcs
            )
          )
          .finally(() => setLoadingCalcsWsId(null));
      }
    },
    [
      setSelectedWorkspaceId,
      calculationsCache,
      fetchCalculations,
      packageId,
      pushNotification,
      clearNotificationsBySource,
      setLoadingCalcsWsId,
    ]
  );

  // ---------------------------------------------------------------------------
  // handleRename
  // ---------------------------------------------------------------------------

  /**
   * Renames the selected workspace and refetches the workspace list.
   *
   * @param {string} newName - The new workspace name from the modal.
   */
  const handleRename = useCallback(
    async (newName: string) => {
      if (!activeWorkspaceId) return;
      setIsRenameLoading(true);
      try {
        await renameWorkspace(activeWorkspaceId, { name: newName });
        triggerWorkspaces();
        setIsRenameModalOpen(false);
        // Wipe stale rename errors from prior failed attempts.
        clearNotificationsBySource(NOTIFICATION_SOURCES.workspaceRename);
      } catch {
        pushNotification(
          'Failed to rename workspace',
          NOTIFICATION_SOURCES.workspaceRename
        );
      } finally {
        setIsRenameLoading(false);
      }
    },
    [
      activeWorkspaceId,
      renameWorkspace,
      triggerWorkspaces,
      pushNotification,
      clearNotificationsBySource,
    ]
  );

  return {
    handleCreateWorkspace,
    handleCalculate,
    handleSave,
    handleWorkspaceExpand,
    handleRename,
    isCreateLoading,
    isSaveLoading,
    isRenameLoading,
    isCreateModalOpen,
    setIsCreateModalOpen,
    isSaveModalOpen,
    setIsSaveModalOpen,
    isRenameModalOpen,
    setIsRenameModalOpen,
  };
};
